import { Paperclip } from "@phosphor-icons/react";
import { useRef } from "react";
import { toast } from "react-toastify";

import { Button } from "common/components/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";

import type React from "react";

interface FileInputProps {
  isDisabled?: boolean;
  onFileUpload: (file: File) => Promise<void>;
  currentFileCount: number;
}

const ALLOWED_MIME_TYPES = [
  "application/msword", // DOC
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX
  "text/csv", // CSV
  "application/vnd.ms-excel", // XLS
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
  "application/vnd.ms-powerpoint", // PPT
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // PPTX
  "application/pdf", // PDF
  "text/plain", // TXT
  "image/jpeg", // JPEG/JPG
  "image/gif", // GIF
  "image/bmp", // BMP
  "image/png", // PNG
  "video/mp4", // MP4
];

const ALLOWED_EXTENSIONS = [
  ".doc",
  ".docx",
  ".csv",
  ".xls",
  ".xlsx",
  ".ppt",
  ".pptx",
  ".pdf",
  ".txt",
  ".jpeg",
  ".jpg",
  ".gif",
  ".bmp",
  ".png",
  ".mp4",
];

const ACCEPT_STRING = [...ALLOWED_EXTENSIONS, ...ALLOWED_MIME_TYPES].join(",");
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILES_LIMIT = 6;

export default function FileInput({
  isDisabled = false,
  onFileUpload,
  currentFileCount,
}: FileInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files || []);
    const availableSlots = MAX_FILES_LIMIT - currentFileCount;

    if (files.length > availableSlots) {
      toast.error(`Maximum number of files ${MAX_FILES_LIMIT} reached`);
    }

    files.slice(0, availableSlots).forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(
          `File ${file.name} is too large. Maximum size is ${
            MAX_FILE_SIZE / 1024 / 1024
          }MB.`,
        );
        return;
      }

      // File type validation
      const isAllowedType = ALLOWED_MIME_TYPES.includes(file.type);
      const isAllowedExtension = ALLOWED_EXTENSIONS.some((ext) =>
        file.name.toLowerCase().endsWith(ext),
      );

      if (!isAllowedType && !isAllowedExtension) {
        toast.error(`File type not supported: ${file.name}`);
        return;
      }

      onFileUpload(file);
    });

    const input = event.target as HTMLInputElement;
    input.value = "";
  }

  const isLimitReached = currentFileCount >= MAX_FILES_LIMIT;

  const triggerButton = (
    <Button
      variant="ghost-black"
      size="xs"
      intent="iconOnly"
      onClick={() => fileInputRef.current?.click()}
      disabled={isDisabled || isLimitReached}
    >
      <Paperclip />
    </Button>
  );

  return (
    <>
      {isLimitReached ? (
        <Tooltip>
          <TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
          <TooltipContent>
            You&apos;ve added the maximum number of attachments (6)
          </TooltipContent>
        </Tooltip>
      ) : (
        triggerButton
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        onChange={handleFileSelect}
        accept={ACCEPT_STRING}
      />
    </>
  );
}
